import React from 'react';

import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { useCreateProjectDuplicationProcessMutation } from 'components/hooks/projectMutations';
import useForm from 'components/hooks/useForm';
import useRequestError from 'components/hooks/useRequestError';

import FilledButton from 'components/application/buttons/FilledButton';
import TextButton from 'components/application/buttons/TextButton';
import ErrorMessage from 'components/application/ErrorMessage';
import ModalBox from 'components/application/ModalBox';
import TextField from 'components/application/TextField';

import { useCurrentActor } from 'components/contexts/CurrentActor';
import { useBreadBoard } from 'components/contexts/Toaster';

const initialDuplicateProject = { name: '' };

export default function RequestDuplicationModal({
  onClose,
  onStartDuplication,
  originalProject,
}) {
  const breadBoard = useBreadBoard();

  const {
    division: {
      attributes: { singularRAMSName },
    },
  } = useCurrentActor();

  const [duplicateProject, , handleDuplicateProjectInputChange] = useForm(
    initialDuplicateProject,
  );

  const [requestError, , removeErrorStyling, , handleRequestError] =
    useRequestError();

  const createProjectDuplicationProcessMutation =
    useCreateProjectDuplicationProcessMutation();

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'projectsDashboard.components.RequestDuplicationModal',
  });

  const { t: i18nTShared } = useTranslation('common', {
    keyPrefix: 'shared',
  });

  const handleDuplicateProjectSubmit = () => {
    createProjectDuplicationProcessMutation.mutate(
      {
        id: originalProject.id,
        duplicationProcessParams: { name: duplicateProject.name },
      },
      {
        onSuccess: (data) => {
          onStartDuplication({ duplicationProcessId: data.data.id });
        },
        onError: (error) => {
          if (error.response.status === 422) {
            handleRequestError(error);
          } else {
            breadBoard.addInedibleToast({
              fullMessage: i18nTShared('errors.duplication', {
                interpolation: { escapeValue: false },
                name: originalProject.attributes.name,
              }),
            });
          }
        },
      },
    );
  };

  return (
    <ModalBox
      customFooter={
        <div className='modalbox__footer-actions modalbox__footer-actions--right'>
          <TextButton
            className='tw-mr-3'
            color='blue'
            onClick={() => onClose({ projectId: originalProject.id })}
            size='md'
          >
            {i18nTShared('cancel')}
          </TextButton>
          <FilledButton
            color='mint'
            disabled={
              duplicateProject.name.length === 0 ||
              createProjectDuplicationProcessMutation.isLoading
            }
            onClick={handleDuplicateProjectSubmit}
          >
            {i18nT('button')}
          </FilledButton>
        </div>
      }
      isOpen={true}
      mode='letterbox'
      onClose={() => onClose({ projectId: originalProject.id })}
    >
      <>
        <h2 className='modalbox-header__title truncated-text-container tw-border-0 tw-border-b-1 tw-border-solid tw-border-grey-100 tw-p-6 tw-text-xl tw-font-semibold tw-text-grey-900'>
          {i18nTShared('duplicateName', {
            interpolation: { escapeValue: false },
            name: originalProject.attributes.name,
          })}
        </h2>
        <div className='modalbox-body'>
          <ErrorMessage
            isFallback={false}
            modifiers='tw-mb-5'
            validationErrors={requestError.validationErrors}
          />
          <TextField
            autoFocus={true}
            fieldError={requestError.validationErrors.name}
            inputClassName='tw-px-3 tw-py-2.5'
            isRequired={true}
            label={i18nT('textField.label', {
              interpolation: { escapeValue: false },
              singularRAMSName,
            })}
            name='name'
            onChange={handleDuplicateProjectInputChange}
            removeErrorStyling={removeErrorStyling}
            value={duplicateProject.name}
          />
        </div>
      </>
    </ModalBox>
  );
}

RequestDuplicationModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  onStartDuplication: PropTypes.func.isRequired,
  originalProject: PropTypes.object.isRequired,
};
